
import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';
import { useToast } from '@/hooks/use-toast';
import { AuthContextType } from '@/types/auth.types';
import { authService } from '@/services/authService';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user || null);
      setIsLoading(false);
    });

    const getSession = async () => {
      try {
        const { data, error } = await authService.getSession();
        
        if (error) {
          setIsLoading(false);
          return;
        }

        setSession(data.session);
        setUser(data.session?.user || null);
      } catch {
        // Session retrieval failed silently
      } finally {
        setIsLoading(false);
      }
    };

    getSession();

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const result = await authService.signIn(email, password);
    
    if (result.toastOptions) {
      toast(result.toastOptions);
    }
    
    return { success: result.success, error: result.error };
  };

  const signUp = async (email: string, password: string) => {
    const result = await authService.signUp(email, password);
    
    if (result.toastOptions) {
      toast(result.toastOptions);
    }
    
    return { success: result.success, error: result.error };
  };

  const signOut = async () => {
    const result = await authService.signOut();
    
    if (result.toastOptions) {
      toast(result.toastOptions);
    }
    
    window.location.href = '/auth';
  };

  const signInWithProvider = async (provider: string) => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider as any,
        options: {
          redirectTo: `${window.location.origin}/`
        }
      });
      
      if (error) {
        toast({
          title: "Error de autenticación",
          description: error.message,
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "Error inesperado al autenticar",
        variant: "destructive",
      });
    }
  };

  const value = {
    user,
    session,
    isLoading,
    signIn,
    signUp,
    signOut,
    signInWithProvider,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};
